import { useCallback, useMemo, useState } from 'react';

import { Form } from 'antd';
import type { FormInstance } from 'antd';

import { type ConnectorConfig, type ConnectorField, normalizeConnectorConfigByFields } from '@/entities/connector';
import type { UpdateConnectorRequest } from '@/entities/connector';

type PartialConfigChangeParams = {
  config: ConnectorConfig;
  invalidMappedKeys: string[];
};

type UseConfigJsonBridgeParams = {
  form: FormInstance<UpdateConnectorRequest>;
  schemaFields: readonly ConnectorField[];
  selectedType?: string;
  initialConfig?: ConnectorConfig;
};

export const useConfigJsonBridge = ({ form, schemaFields, initialConfig }: UseConfigJsonBridgeParams) => {
  const formConfig = Form.useWatch('config', form);

  const [jsonUnmappedConfig, setJsonUnmappedConfig] = useState<ConnectorConfig>({});
  const [hasJsonError, setHasJsonError] = useState(false);

  const schemaFieldKeySet = useMemo(() => new Set(schemaFields.map((field) => field.label)), [schemaFields]);
  const schemaCompleteConfig = useMemo(
    () => Object.fromEntries(schemaFields.map((field) => [field.label, ''])) as ConnectorConfig,
    [schemaFields]
  );

  const normalizeMappedSchemaConfig = useCallback(
    (mappedConfig?: ConnectorConfig) =>
      normalizeConnectorConfigByFields(schemaFields, {
        ...schemaCompleteConfig,
        ...(mappedConfig ?? {}),
      }),
    [schemaCompleteConfig, schemaFields]
  );

  const jsonDisplayConfig = useMemo(
    () =>
      normalizeConnectorConfigByFields(schemaFields, {
        ...schemaCompleteConfig,
        ...(formConfig === undefined ? (initialConfig ?? {}) : formConfig),
        ...jsonUnmappedConfig,
      }),
    [initialConfig, formConfig, jsonUnmappedConfig, schemaCompleteConfig, schemaFields]
  );

  const syncMappedConfigFields = useCallback(
    (mappedConfig: ConnectorConfig, clearErrors: boolean) => {
      const normalizedMappedConfig = normalizeMappedSchemaConfig(mappedConfig);

      form.setFieldValue('config', normalizedMappedConfig);
      form.setFields(
        schemaFields.map((field) => ({
          name: ['config', field.label],
          value: normalizedMappedConfig[field.label],
          errors: clearErrors ? [] : undefined,
          warnings: clearErrors ? [] : undefined,
        }))
      );
    },
    [form, normalizeMappedSchemaConfig, schemaFields]
  );

  const getCurrentMappedConfig = useCallback(
    () => normalizeMappedSchemaConfig(formConfig === undefined ? (initialConfig ?? {}) : formConfig),
    [formConfig, initialConfig, normalizeMappedSchemaConfig]
  );

  const handleConfigChange = useCallback(
    (config: ConnectorConfig) => {
      const mappedConfig: ConnectorConfig = {};
      const unmappedConfig: ConnectorConfig = {};

      for (const [key, value] of Object.entries(config ?? {})) {
        if (schemaFieldKeySet.has(key)) {
          mappedConfig[key] = value;
          continue;
        }
        unmappedConfig[key] = value;
      }

      syncMappedConfigFields(mappedConfig, true);
      setJsonUnmappedConfig(unmappedConfig);
    },
    [schemaFieldKeySet, setJsonUnmappedConfig, syncMappedConfigFields]
  );

  const handlePartialConfigChange = useCallback(
    ({ config, invalidMappedKeys }: PartialConfigChangeParams) => {
      const currentMappedConfig = getCurrentMappedConfig();
      const invalidMappedKeySet = new Set(invalidMappedKeys);
      const nextMappedConfig: ConnectorConfig = { ...currentMappedConfig };
      const nextUnmappedConfig: ConnectorConfig = {};

      for (const field of schemaFields) {
        const hasFieldInConfig = field.label in config;

        if (!hasFieldInConfig) {
          nextMappedConfig[field.label] = '';
          continue;
        }

        if (invalidMappedKeySet.has(field.label)) {
          continue;
        }

        nextMappedConfig[field.label] = config[field.label];
      }

      for (const [key, value] of Object.entries(config)) {
        if (schemaFieldKeySet.has(key)) {
          continue;
        }

        nextUnmappedConfig[key] = value;
      }

      syncMappedConfigFields(nextMappedConfig, true);
      setJsonUnmappedConfig(nextUnmappedConfig);
    },
    [getCurrentMappedConfig, schemaFieldKeySet, schemaFields, syncMappedConfigFields]
  );

  const handleJsonErrorChange = useCallback((hasError: boolean) => {
    setHasJsonError(hasError);
  }, []);

  const resetJsonBridge = useCallback(
    (nextConfig?: ConnectorConfig) => {
      const mappedConfig: ConnectorConfig = {};
      const unmappedConfig: ConnectorConfig = {};

      for (const [key, value] of Object.entries(nextConfig ?? {})) {
        if (schemaFieldKeySet.has(key)) {
          mappedConfig[key] = value;
          continue;
        }
        unmappedConfig[key] = value;
      }

      setJsonUnmappedConfig(unmappedConfig);
      syncMappedConfigFields(mappedConfig, true);
      setHasJsonError(false);
    },
    [schemaFieldKeySet, syncMappedConfigFields]
  );

  return {
    watchedFormConfig: formConfig,
    jsonDisplayConfig,
    jsonUnmappedConfig,
    hasJsonError,
    handleConfigChange,
    handlePartialConfigChange,
    handleJsonErrorChange,
    resetJsonBridge,
  };
};
